import type { VerlaufEintrag } from "../v3/verlauf";
import type { MailLinkDto } from "../../../services/mailLinkService";
import type { CallLogDto } from "../../../services/telephonyService";
import type { DocumentDto } from "../../../services/documentService";
import type { AppUser } from "../../../services/userService";
import type { VorgangTerminDto } from "../../../services/calendarTerminService";
import { toGraphDate } from "../../../utils/graphDatum";
import { graphAllDayDate } from "../../calendar/allDay";
import {
    DIRECTION_LABEL,
    displayNameOf,
    isMissedInbound,
    statusLabel,
} from "../../telephony/telephonyUtils";
import { htmlZuKlartext } from "./klartext";
import type { MailFileAddress, ParsedMailFile } from "../../../services/mailFileService";

/**
 * Mail, Anruf, Dokument und — seit PO 14.09.2026 — Termin als BLÖCKE des
 * Notizbereichs (Zeichnung 10.09.2026) — reine Ableitung, bewusst ohne React.
 *
 * ══ WARUM NICHT ALS NOTIZ ══════════════════════════════════════════════════
 * Ein Ereignis wird NICHT in die Notiztabelle geschrieben. Es wird nur an
 * seiner zeitlichen Stelle mitgerendert. Stünde eine Kopie von Betreff,
 * Absender und Anriss in der Notiz, veraltete sie still — dieselbe Regel wie
 * bei den `#`-Bezügen (`bezugDarstellung.ts`).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Was der Blockkopf zeigt, steht hier in `wer`, `kopfTitel` und `zeit`: bei
 * einer Mail der Absender, beim Anruf der Gesprächspartner, beim Dokument der,
 * der es hochgeladen hat (PO 14.09.2026: „oben von wem und wann, in der Karte
 * der Name“). Ist der Hochladende unbekannt — Bestand vor der Spalte
 * `uploadedByUserId`, Ablage aus einem Lauf ohne Nutzer —, steht dort der
 * Kunde, wie es die Zeichnung vom 10.09.2026 verlangte („Kundenname im Header
 * des Blocks“). Beim Termin steht der Kunde, weil der Termin keinen Absender
 * oder Hochladenden hat.
 *
 * ══ NICHTS STEHT DOPPELT (PO 11.09.2026) ═══════════════════════════════════
 * Früher trug die Karte drei Zeilen — Betreff, Von→An, Anriss — und der
 * Absender stand damit zweimal da: im Kopf als `wer` und noch einmal am Anfang
 * der zweiten Zeile. „außerdem ist das doppelt da drin … dann lieber E-Mail
 * Baltes und dann hast du den Betreff in der oberen Zeile daneben stehen. Und
 * dann seh ich sofort den Inhalt.“
 *
 * Seitdem gilt: der KOPF trägt die Kennung (`wer` + `kopfTitel`), die KARTE
 * trägt nur noch den Inhalt (`inhalt`). Was dabei aus der Ansicht fällt — der
 * Empfänger der Mail, der Vorschautext eines Dokuments — steht in der
 * Hover-Vorschau, die an der Karte hängt.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export type EreignisArt = "mail" | "anruf" | "dokument" | "termin";

export interface Ereignis {
    /** Stabiler Schlüssel — derselbe wie am Verlaufseintrag. */
    key: string;
    art: EreignisArt;
    /** Zeitpunkt; Sortierschlüssel im gemischten Strom UND Anzeige. */
    zeit: Date;
    /** Wer im Blockkopf steht. */
    wer: string;
    /**
     * Was im Blockkopf NEBEN `wer` steht — die Kennung des Ereignisses:
     * bei der Mail der Betreff, beim Anruf Richtung·Status·Dauer, beim
     * Dokument Typ·Größe. LEER ist erlaubt (Dokument ohne Endung und Größe);
     * die Karte lässt das Stück dann samt Trennpunkt weg.
     */
    kopfTitel: string;
    /**
     * Die EINZIGE Zeile der Karte — der Inhalt, den man sofort sehen will:
     * der Mail-Anriss, die Gesprächsnotiz, der Dateiname (PO 14.09.2026).
     *
     * FEHLT er, heißt das NICHT „leere Karte“: die Karte der Mail führt auch
     * textlos weiter (Klick und Hover-Vorschau), die des Anrufs führt
     * nirgendwohin und wird dann gar nicht gezeichnet. Die Entscheidung trifft
     * `EreignisKarte`; hier steht nur die Wahrheit über die Daten — und die
     * ist: es gibt keinen Inhalt.
     */
    inhalt?: string;
    /** Auffällig: verpasster Anruf. Die Farbe trägt es nicht allein — der Text sagt es auch. */
    warnung?: boolean;
    mail?: MailLinkDto;
    anruf?: CallLogDto;
    dokument?: DocumentDto;
    termin?: VorgangTerminDto;
}

/**
 * Gesprächsdauer knapp — „4:12 Min.“; LEER, wenn keine bekannt ist.
 * Bewusst nicht `fmtDuration` aus der Telefonie: die liefert „—“, und in einer
 * mit „·“ verketteten Zeile stünde dann ein Gedankenstrich zwischen zwei
 * Angaben, als fehlte dort etwas.
 */
export function dauerKurz(sek?: number | null): string {
    if (!sek || sek <= 0) return "";
    const m = Math.floor(sek / 60);
    const s = sek % 60;
    return m ? `${m}:${String(s).padStart(2, "0")} Min.` : `${s} Sek.`;
}

/** Dateigröße knapp — „412 kB“, nicht „412.331 Bytes“. */
export function dateiGroesse(bytes?: number | null): string {
    if (!bytes || bytes <= 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
    return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

/** Kurzform des MIME-Typs — „PDF“, „DOCX“; leer, wenn nichts abzuleiten ist. */
export function dateiTyp(d: DocumentDto): string {
    const ausName = /\.([a-z0-9]{1,5})$/i.exec(d.fileName ?? "");
    if (ausName) return ausName[1].toUpperCase();
    const mime = (d.mimeType ?? "").split("/").pop() ?? "";
    return mime ? mime.toUpperCase() : "";
}

function ausMail(e: VerlaufEintrag, m: MailLinkDto): Ereignis {
    const von = m.senderName || m.senderEmail || "Unbekannt";
    return {
        key: e.key,
        art: "mail",
        zeit: e.at,
        wer: von,
        kopfTitel: m.subject?.trim() || "(Kein Betreff)",
        // Der Empfänger stand hier einmal als „Von → An“. Er ist ersatzlos
        // gestrichen: der Absender stand damit doppelt im Block, und die
        // Empfängerliste ist in der Hover-Vorschau vollständig zu sehen.
        ...(m.preview?.trim() ? { inhalt: m.preview.trim() } : {}),
        mail: m,
    };
}

const mailDateiAdresse = (adresse: MailFileAddress | null | undefined): string => {
    if (!adresse) return "Maildatei";
    if (adresse.name && adresse.address) return `${adresse.name} <${adresse.address}>`;
    return adresse.name || adresse.address || "Maildatei";
};

/** Lokale `.eml`/`.msg`: Maildarstellung, aber weiterhin Dokument-Nutzlast. */
function ausMailDatei(
    e: VerlaufEintrag,
    d: DocumentDto,
    mail: ParsedMailFile | null | undefined,
): Ereignis {
    const text = kuerze(htmlZuKlartext(mail?.bodyHtml ?? ""), 320);
    return {
        key: e.key,
        art: "mail",
        zeit: e.at,
        wer: mailDateiAdresse(mail?.from),
        kopfTitel: mail?.subject?.trim() || d.fileName?.trim() || "(Kein Betreff)",
        ...(text ? { inhalt: text } : {}),
        dokument: d,
    };
}

/**
 * Richtung samt dem Kollegen, der telefoniert hat — „Ausgehend von Sandra
 * Kessler“, „Eingehend bei Sandra Kessler“. `wer` ist beim Anruf der
 * Gesprächspartner draußen; WER VON UNS am Apparat war, stand bis 28.09.2026
 * nirgends auf der Karte (nur in der Archivsuche). Gerade bei der
 * Gesprächsnotiz will man es wissen: sie ist die Mitschrift genau dieses
 * Kollegen. Ohne bekannten Nutzer (Zentrale, Importe) bleibt die reine
 * Richtung stehen.
 */
export function anrufRichtung(c: Pick<CallLogDto, "direction" | "userName">): string {
    const richtung = DIRECTION_LABEL[c.direction] ?? "";
    const nutzer = c.userName?.trim();
    if (!nutzer) return richtung;
    if (c.direction === "outbound") return `${richtung} von ${nutzer}`;
    if (c.direction === "inbound") return `${richtung} bei ${nutzer}`;
    return [richtung, nutzer].filter(Boolean).join(" · ");
}

function ausAnruf(e: VerlaufEintrag, c: CallLogDto): Ereignis {
    const verpasst = isMissedInbound(c) && !c.resolvedAt;
    const dauer = dauerKurz(c.talkDurationSec ?? c.callDurationSec);
    const teile = [
        anrufRichtung(c),
        verpasst ? "verpasst" : statusLabel(c.status),
        dauer,
    ].filter(Boolean);
    return {
        key: e.key,
        art: "anruf",
        zeit: e.at,
        wer: displayNameOf(c),
        // „verpasst“ steht WEITERHIN im Text, nicht nur in der Farbe — der
        // Kopf ist jetzt die Stelle, an der es steht.
        kopfTitel: teile.join(" · ") || "Anruf",
        // Ohne Gesprächsnotiz bleibt `inhalt` leer — und das ist der
        // REGELFALL, nicht die Ausnahme: aus Yeastar importierte Anrufe
        // bringen keine Notiz mit, ein verpasster Anruf hat definitionsgemäß
        // keine. Hier wird deshalb NICHTS erfunden („(keine Notiz)“ wäre eine
        // Behauptung über nichts); die Karte entfällt dann in der Anzeige.
        ...(c.notes?.trim() ? { inhalt: c.notes.trim() } : {}),
        ...(verpasst ? { warnung: true } : {}),
        anruf: c,
    };
}

/**
 * Anzeigename eines Nutzers — „Sandra Kessler“, dieselbe Form wie im Kopf des
 * Notizblocks (`BlockKopf.nameVon`), dessen Zwilling der Ereignisblock ist.
 * Bewusst nicht `labelForUserKey`: das liefert „Nachname, Vorname“ und bei
 * noch leerer Nutzerliste „…“ — hier soll dann der Kunde stehen, nicht drei
 * Punkte. Unbekannte Ids ergeben `null`, und der Aufrufer fällt zurück.
 */
function nutzerName(users: AppUser[], id: string | null | undefined): string | null {
    if (!id) return null;
    const u = users.find((x) => x.id === id);
    if (!u) return null;
    const fl = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
    return fl || u.name || u.username || u.email || null;
}

function ausDokument(e: VerlaufEintrag, d: DocumentDto, kunde: string | null, users: AppUser[]): Ereignis {
    const name = d.fileName?.trim() || `Dokument ${d.id}`;
    return {
        key: e.key,
        art: "dokument",
        zeit: e.at,
        // PO 14.09.2026: „die Dokument-Karte anders als die Mail-Karte: oben
        // von wem und wann, in der Karte der Name“. Der Hochladende steht seit
        // der Spalte `uploadedByUserId` am Dokument; fehlt er (Bestand davor,
        // Ablage ohne Nutzer), bleibt der Kunde — die Antwort der Zeichnung
        // vom 10.09.2026 auf „zu wem gehört das“.
        wer: nutzerName(users, d.uploadedByUserId) || kunde || "Dokument",
        // Nur noch Typ und Größe im Kopf — der Dateiname ist der INHALT der
        // Karte, so wie der Anriss der Inhalt der Mailkarte ist. Bis zum
        // 14.09. stand er im Kopf, und die Karte zeigte nur die Ikone; der PO
        // wollte den Namen in der Karte sehen, dort, wo bei der Mail der Text
        // steht.
        kopfTitel: [dateiTyp(d), dateiGroesse(d.fileSize)].filter(Boolean).join(" · "),
        inhalt: name,
        dokument: d,
    };
}

/** Zwei Ziffern — „08", „14". */
const zz = (n: number): string => String(n).padStart(2, "0");
const uhr = (d: Date): string => `${zz(d.getHours())}:${zz(d.getMinutes())}`;
const tagMonat = (d: Date): string => `${zz(d.getDate())}.${zz(d.getMonth() + 1)}.`;

/**
 * Der Zeitraum eines Termins, so knapp wie möglich — „08:00 – 09:00",
 * „ganztägig", über Nacht „14.09. 22:00 – 15.09. 01:00", mehrtägig ganztägig
 * „ganztägig, 14.09. – 16.09.". LEER ohne Startzeit (kein Kalender-Leserecht).
 *
 * Nicht `zeitraumLabel` aus `calendar/peek.ts`: das schreibt den vollen Tag
 * mit Wochentag und Jahr — und der Tag steht am Ereignisblock schon rechts im
 * Kopf (`datumKurz(zeit)`, der Terminbeginn). NICHTS STEHT DOPPELT (PO
 * 11.09.2026): hier bleibt nur, was der Kopf nicht ohnehin sagt.
 *
 * Ganztägige Termine kommen von Graph als Mitternacht UTC; `graphAllDayDate`
 * liest nur den Datumsteil, sonst rutschte das Ende einen Tag zu weit. Das
 * Graph-Ende eines Ganztagstermins ist EXKLUSIV — der letzte Tag ist der
 * davor.
 */
export function terminZeitraumKurz(
    t: Pick<VorgangTerminDto, "start" | "end" | "isAllDay">,
): string {
    if (!t.start) return "";
    if (t.isAllDay) {
        const s = graphAllDayDate(t.start);
        const eExkl = t.end ? graphAllDayDate(t.end) : null;
        const e = eExkl ? new Date(eExkl.getTime() - 24 * 60 * 60 * 1000) : s;
        if (Number.isNaN(s.getTime())) return "";
        return e.getTime() > s.getTime() ? `ganztägig, ${tagMonat(s)} – ${tagMonat(e)}` : "ganztägig";
    }
    const s = toGraphDate(t.start);
    if (Number.isNaN(s.getTime())) return "";
    if (!t.end) return uhr(s);
    const e = toGraphDate(t.end);
    if (Number.isNaN(e.getTime())) return uhr(s);
    const selberTag =
        s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth() && s.getDate() === e.getDate();
    return selberTag ? `${uhr(s)} – ${uhr(e)}` : `${tagMonat(s)} ${uhr(s)} – ${tagMonat(e)} ${uhr(e)}`;
}

/** Erste `max` Zeichen, an einer Wortgrenze abgeschnitten; leer bleibt leer. */
function kuerze(s: string, max: number): string {
    if (s.length <= max) return s;
    const schnitt = s.slice(0, max);
    const luecke = schnitt.lastIndexOf(" ");
    return `${(luecke > max * 0.6 ? schnitt.slice(0, luecke) : schnitt).trimEnd()} …`;
}

/** So viel Termin-Notiz steht auf der Karte; die Karte klemmt ohnehin auf drei Zeilen. */
const TERMIN_AUSZUG_ZEICHEN = 320;

/** T-220: Text der Karte eines vertraulichen Termins (Kalender eines Kollegen). */
export const TERMIN_VERTRAULICH_HINWEIS =
    "Dieser Termin ist in Outlook als vertraulich markiert. Die Notiz dazu sieht nur, wer den Kalender lesen darf.";

/**
 * Der Termin als Ereignis (PO 14.09.2026: „Termin-Notiz, die einem Vorgang
 * zugewiesen wird, erscheint im Vorgang"; seit 27.10.2026 auch ohne Notiz,
 * siehe `terminImVorgang`).
 *
 * Der Kopf: Stempel „Termin", `wer` = der Kunde (wie beim Dokument — ein
 * Termin hat keinen Absender, und der Kunde ist die Antwort auf „zu wem
 * gehört das"), `kopfTitel` = Betreff · Zeitraum. Die Karte: der TEXTAUSZUG
 * der Notiz-Seite. Nur Text — die Seite selbst trägt Listen, Tabellen und den
 * Freihand-Layer, und das alles zeigt nur sie; der Klick führt hin. Trägt die
 * Seite NUR eine Zeichnung, sagt die Karte genau das, statt leer zu bleiben.
 * Ohne Notiz bleibt die Karte leer: Betreff und Zeitraum im Kopf genügen.
 *
 * `contentHtml` ist auf beiden Seiten sanitisiert (Server: `sanitizeMailHtml`,
 * Seite: `reinigeBlockHtml`) und wird hier trotzdem nur als Klartext
 * weitergereicht — auf der Karte kommt kein Markup an.
 */
function ausTermin(e: VerlaufEintrag, t: VorgangTerminDto, kunde: string | null): Ereignis {
    const zeitraum = terminZeitraumKurz(t);
    if (t.vertraulich) {
        // T-220: Der Termin ist in Outlook als vertraulich markiert und liegt im
        // Kalender eines Kollegen — er bleibt zu, aber nicht stillschweigend.
        return {
            key: e.key,
            art: "termin",
            zeit: e.at,
            wer: kunde || "Termin",
            kopfTitel: ["Vertraulicher Termin", zeitraum].filter(Boolean).join(" · "),
            // Der Hinweis spricht von „der Notiz" — ohne Notiz wäre er falsch.
            ...(t.notizVorhanden ? { inhalt: TERMIN_VERTRAULICH_HINWEIS } : {}),
            termin: t,
        };
    }
    const betreff = t.subject?.trim() || "(ohne Betreff)";
    const text = kuerze(htmlZuKlartext(t.notizSeite?.contentHtml ?? ""), TERMIN_AUSZUG_ZEICHEN);
    const inhalt = text || (t.notizSeite?.hatZeichnung ? "Handschriftliche Notiz (Zeichnung)" : "");
    return {
        key: e.key,
        art: "termin",
        zeit: e.at,
        wer: kunde || "Termin",
        kopfTitel: [betreff, zeitraum].filter(Boolean).join(" · "),
        ...(inhalt ? { inhalt } : {}),
        termin: t,
    };
}

/**
 * Die Kennung, unter der man das Ereignis NENNT — im Archiv als Titel, an der
 * Sprechblase als „Kommentare zu: …“. Bei Mail und Anruf ist das der
 * `kopfTitel`; beim Dokument steht der Name seit dem 14.09.2026 in der Karte,
 * und Typ·Größe allein („PDF · 412 kB“) nennen nichts. Darum hier wieder
 * zusammengesetzt: „Wertaufstellung.pdf · PDF · 412 kB“ — wörtlich, was vor
 * dem Umbau im Kopf stand.
 */
export function ereignisKennung(e: Ereignis): string {
    if (e.art !== "dokument") return e.kopfTitel;
    return [e.inhalt, e.kopfTitel].filter(Boolean).join(" · ");
}

/**
 * Die Ereignisse eines Arbeitsstrangs, ÄLTESTE zuerst — die Ordnung des
 * BLOCKMODELLS, in das sie einsortiert werden (`mischeBloecke` erwartet die
 * aufsteigende Folge). Gelesen wird der Notizbereich mit `neuesteOben` von der
 * anderen Seite; das Umdrehen übernimmt die Fläche beim Zeichnen, nicht diese
 * Ableitung. `baueVerlauf` sortiert absteigend, weil die alte Zeitachse von
 * oben gelesen wurde; das Archiv (`archivZeilen`) sortiert für sich selbst.
 *
 * `users` löst den Hochladenden eines Dokuments zum Namen auf; ohne die Liste
 * (Archiv, ältere Aufrufer) steht beim Dokument der Kunde im Kopf.
 */
export function ereignisseAus(
    eintraege: VerlaufEintrag[],
    kundeName: string | null,
    users: AppUser[] = [],
): Ereignis[] {
    const out: Ereignis[] = [];
    for (const e of eintraege) {
        if (e.mail) out.push(ausMail(e, e.mail));
        else if (e.anruf) out.push(ausAnruf(e, e.anruf));
        else if (e.dokument) {
            out.push(
                e.art === "mail"
                    ? ausMailDatei(e, e.dokument, e.mailDatei)
                    : ausDokument(e, e.dokument, kundeName, users),
            );
        }
        else if (e.termin) out.push(ausTermin(e, e.termin, kundeName));
    }
    return out.sort((a, b) => a.zeit.getTime() - b.zeit.getTime() || a.key.localeCompare(b.key));
}
